<template>
  <div class="tax-modal-overlay">
    <div class="tax-modal-container">

      <!-- STEP 1: Notice Modal -->
      <div v-if="step === 1" class="text-center">
        <h2 class="modal-title">Information Update Required</h2>
        <p class="modal-desc">
          Please upload your latest Company Tax photo to help us maintain accurate account information and comply with regulatory requirements.
        </p>

        <div class="d-flex flex-column align-items-center mt-4">
          <button class="btn-update-now" @click="step = 2">
            Update Now
          </button>
          <button class="btn-skip-link mt-3" @click="handleSkip">
            Skip
          </button>
        </div>
      </div>

      <!-- STEP 2 & 3: Upload Modal (Empty or Selected) -->
      <div v-else-if="step === 2 || step === 3" class="text-start">
        <h3 class="fw-bold mb-1 text-center" style="color: #0f2b3c;">Upload Company Tax Photo</h3>
        <p class="text-muted small mb-3 text-center">
          Please upload a clear photo of your Company Tax for account information update.
        </p>

        <!-- Drag & Drop / Upload Area (Step 2: No File Selected) -->
        <div
          v-if="!selectedFile"
          class="upload-dropzone p-4 text-center mb-3 rounded-3"
          :class="{ 'dragging': isDragging }"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleFileDrop"
          style="background-color: #f0fdf4; border: 2px dashed #bbf7d0;"
        >
          <div class="mb-2">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#27ae60" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <p class="fw-bold mb-1 text-dark small">Drag & drop your file here</p>
          <p class="text-muted small mb-2">or</p>
          <input
            type="file"
            ref="fileInput"
            class="d-none"
            accept=".jpg,.jpeg,.png,.pdf"
            @change="handleFileSelect"
          />
          <button
            class="btn btn-outline-primary btn-sm px-4 mb-2 rounded-3 text-capitalize fw-semibold"
            style="border-color: #8b5cf6; color: #7c3aed; background: #fff;"
            @click="$refs.fileInput.click()"
          >
            Choose File
          </button>
          <p class="text-muted extra-small mb-0" style="font-size: 11px;">JPG, PNG or PDF with max file 5MB</p>
        </div>

        <!-- Preview Area (Step 3: File Selected) -->
        <div
          v-else
          class="upload-preview-box p-3 mb-3 rounded-3"
          style="background-color: #f0fdf4; border: 2px solid #3b82f6;"
        >
          <div v-if="previewUrl && isImage" class="text-center mb-3">
            <img :src="previewUrl" alt="Preview" class="img-fluid rounded border" style="max-height: 140px; object-fit: contain;" />
          </div>
          <div v-else class="text-center py-3 mb-2">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="1.8">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
          </div>

          <div class="d-flex align-items-center justify-content-center mb-3 text-truncate">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#3b82f6" class="me-1 flex-shrink-0">
              <circle cx="12" cy="12" r="10" fill="#3b82f6"/>
              <path d="M8 12l3 3 5-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="fw-semibold small text-dark me-1 text-truncate" style="max-width: 180px;">{{ selectedFile.name }}</span>
            <span class="text-muted small">({{ formatFileSize(selectedFile.size) }})</span>
          </div>

          <div class="d-flex justify-content-center gap-2">
            <button class="btn btn-sm btn-white border px-3 rounded-2 text-primary bg-white fw-semibold" @click="$refs.fileInput.click()">
              Replace
            </button>
            <button class="btn btn-sm btn-white border px-3 rounded-2 text-danger bg-white fw-semibold" @click="removeFile">
              Remove
            </button>
          </div>
        </div>

        <small v-if="errorMessage" class="text-danger d-block mb-2 text-center">{{ errorMessage }}</small>

        <!-- Instructions -->
        <div class="mb-3 ps-1">
          <p class="fw-semibold text-dark small mb-1">Before you upload please make sure</p>
          <ul class="text-muted small ps-3 mb-0" style="font-size: 12px; line-height: 1.6;">
            <li>Make sure all information are clearly visible</li>
            <li>Avoid blurry or cropped image</li>
            <li>Use the latest Company Tax image</li>
          </ul>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex gap-2 mt-4">
          <button class="btn btn-outline-secondary btn-md px-4 rounded-3 flex-grow-1" @click="handleSkip">
            Skip
          </button>
          <button
            class="btn btn-success btn-md px-4 rounded-3 flex-grow-1 text-white fw-bold d-flex align-items-center justify-content-center"
            :class="{ 'disabled opacity-50': !selectedFile || isUploading }"
            :disabled="!selectedFile || isUploading"
            style="background-color: #27ae60; border-color: #27ae60;"
            @click="uploadTaxFile"
          >
            <span v-if="isUploading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <span>Upload</span>
          </button>
        </div>
      </div>

      <!-- STEP 4: Success Modal -->
      <div v-else-if="step === 4" class="text-center py-2">
        <div class="mb-3 d-flex justify-content-center">
          <div class="rounded-circle d-flex align-items-center justify-content-center" style="width: 64px; height: 64px; background-color: #2563eb;">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
        </div>

        <h3 class="fw-bold mb-2" style="color: #0f2b3c;">Company Tax Submitted Successfully!</h3>
        <p class="text-muted small mb-4 px-2">
          Your document currently under review, you can continue using our service as usual.
        </p>

        <div class="mt-4">
          <button class="btn-update-now" @click="handleDone">
            DONE
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { uploadDocument, nextPopup } from "@/api/company";
import { buildFormData } from "@/utils/param-builder";

export default {
  name: "TaxPhotoModal",
  data() {
    return {
      step: 1,
      selectedFile: null,
      previewUrl: null,
      isDragging: false,
      isUploading: false,
      errorMessage: "",
    };
  },
  computed: {
    isImage() {
      return this.selectedFile && this.selectedFile.type.startsWith("image/");
    },
  },
  methods: {
    handleFileSelect(e) {
      const file = e.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    handleFileDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    processFile(file) {
      this.errorMessage = "";
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        this.errorMessage = "Only JPG, PNG, or PDF files are allowed.";
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.errorMessage = "File size exceeds 5MB limit.";
        return;
      }

      this.selectedFile = file;
      if (this.isImage) {
        this.previewUrl = URL.createObjectURL(file);
      } else {
        this.previewUrl = null;
      }
      this.step = 3;
    },
    removeFile() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.selectedFile = null;
      this.previewUrl = null;
      this.errorMessage = "";
      this.step = 2;
    },
    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
    },
    async uploadTaxFile() {
      if (!this.selectedFile) return;

      this.isUploading = true;
      this.errorMessage = "";

      try {
        const formData = new FormData();
        formData.append("document_key", "tax_id_file");
        formData.append("document_file", this.selectedFile);

        const response = await uploadDocument(formData);
        if (response.status === "success" || response.code === 200) {
          this.step = 4;
        } else {
          this.errorMessage = response.message || "Failed to upload file. Please try again.";
        }
      } catch (err) {
        console.error("Upload tax_id_file error:", err);
        this.errorMessage = "An error occurred during upload. Please try again.";
      } finally {
        this.isUploading = false;
      }
    },
    async handleSkip() {
      sessionStorage.setItem("tax_modal_dismissed", "true");
      await this.dismissPopup();
      this.$emit("close");
    },
    async handleDone() {
      sessionStorage.setItem("tax_modal_dismissed", "true");
      await this.dismissPopup();
      this.$emit("close");
    },
    async dismissPopup() {
      try {
        const merchantId = localStorage.getItem("merchantId");
        if (merchantId) {
          const body = {
            merchant_id: merchantId,
            current_popup: "done",
          };
          await nextPopup(buildFormData(body));
        }
      } catch (e) {
        console.error("dismissPopup error:", e);
      }
    },
  },
  beforeUnmount() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
};
</script>

<style scoped>
.tax-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.tax-modal-container {
  background: #ffffff;
  border-radius: 20px;
  width: 90%;
  max-width: 480px;
  padding: 40px 32px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  text-align: center;
}

.modal-title {
  color: #0f2b3c;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.25;
  margin-bottom: 16px;
}

.modal-desc {
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 24px;
  padding: 0 4px;
}

.btn-update-now {
  background-color: #27ae60;
  border: none;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 12px;
  width: 100%;
  transition: background-color 0.2s ease;
}

.btn-update-now:hover {
  background-color: #219150;
}

.btn-skip-link {
  background: none;
  border: none;
  color: #27ae60;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 16px;
  cursor: pointer;
}

.btn-skip-link:hover {
  text-decoration: underline;
}

.extra-small {
  font-size: 11px;
}

.upload-dropzone {
  transition: border-color 0.2s ease;
}

.upload-dropzone.dragging {
  border-color: #2563eb !important;
  background-color: #eff6ff !important;
}
</style>

